/**
 * Originally Mantine's useMediaQuery, see:
 * https://github.com/mantinedev/mantine/blob/master/packages/%40mantine/hooks/src/use-media-query/use-media-query.ts
 */

import { useEffect, useState } from 'react';

export interface UseMediaQueryOptions {
  getInitialValueInEffect: boolean;
}

type MediaQueryCallback = (event: { matches: boolean; media: string }) => void;

/**
 * Older versions of Safari (shipped withCatalina and before) do not support addEventListener on matchMedia
 * https://stackoverflow.com/questions/56466261/matchmedia-addlistener-marked-as-deprecated-addeventlistener-equivalent
 */
function attachMediaListener(query: MediaQueryList, callback: MediaQueryCallback) {
  try {
    query.addEventListener('change', callback);
    return () => query.removeEventListener('change', callback);
  } catch {
    query.addListener(callback);
    return () => query.removeListener(callback);
  }
}

function getInitialValue(query: string, initialValue?: boolean) {
  if (typeof initialValue === 'boolean') {
    return initialValue;
  }

  if (typeof window !== 'undefined' && 'matchMedia' in window) {
    return window.matchMedia(query).matches;
  }

  return false;
}

export function useMediaQuery(
  query: string,
  initialValue?: boolean,
  { getInitialValueInEffect }: UseMediaQueryOptions = {
    getInitialValueInEffect: true,
  },
): boolean {
  const [matches, setMatches] = useState(
    // eslint-disable-next-line react/prefer-use-state-lazy-initialization
    getInitialValueInEffect ? initialValue : getInitialValue(query),
  );

  useEffect(() => {
    try {
      const mediaQuery = window.matchMedia(query);
      // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
      setMatches(mediaQuery.matches);
      return attachMediaListener(mediaQuery, event => setMatches(event.matches));
    } catch {
      // Safari iframe compatibility issue
      return undefined;
    }
  }, [query]);

  return matches || false;
}
